import { GroupStandingsTieBreakerEnum } from '../types/enums';
import { DEFAULT_GROUP_STANDINGS_TIE_BREAKERS } from './groupStandingsTieBreakers';

export type GroupStandingsComparableRow = {
  id: number;
  matchWin?: number;
  teamWin?: number;
  teamDraw?: number;
  teamPoints?: number;
  points?: number;
  pointsWon?: number;
  pointsAgainst?: number;
  gameWin?: number;
  gameLost?: number;
  gameDifference?: number;
  pointsPercentage?: number;
  seed?: number | null;
  h2hWins?: Record<number, number>;
  h2hPointsFor?: Record<number, number>;
  h2hPointsAgainst?: Record<number, number>;
};

type IndexedRow<T extends GroupStandingsComparableRow> = {
  row: T;
  originalIndex: number;
};

const getHeadToHeadWins = (
  row: GroupStandingsComparableRow,
  tiedTeamIds: Set<number>,
): number => {
  let wins = 0;
  const h2hWins = row.h2hWins ?? {};
  for (const id of tiedTeamIds) {
    if (id === row.id) continue;
    wins += h2hWins[id] ?? 0;
  }
  return wins;
};

const getHeadToHeadPointsDiff = (
  row: GroupStandingsComparableRow,
  tiedTeamIds: Set<number>,
): number => {
  let pointsFor = 0;
  let pointsAgainst = 0;
  const h2hPointsFor = row.h2hPointsFor ?? {};
  const h2hPointsAgainst = row.h2hPointsAgainst ?? {};
  for (const id of tiedTeamIds) {
    if (id === row.id) continue;
    pointsFor += h2hPointsFor[id] ?? 0;
    pointsAgainst += h2hPointsAgainst[id] ?? 0;
  }
  return pointsFor - pointsAgainst;
};

const getMetric = (
  row: GroupStandingsComparableRow,
  criterion: GroupStandingsTieBreakerEnum,
  tiedTeamIds: Set<number>,
): number => {
  switch (criterion) {
    case GroupStandingsTieBreakerEnum.TEAM_POINTS:
      return row.teamPoints ?? 0;
    case GroupStandingsTieBreakerEnum.TEAM_WINS:
      return row.teamWin ?? 0;
    case GroupStandingsTieBreakerEnum.TEAM_DRAWS:
      return row.teamDraw ?? 0;
    case GroupStandingsTieBreakerEnum.MATCH_WINS:
      return row.matchWin ?? 0;
    case GroupStandingsTieBreakerEnum.POINTS_DIFFERENCE:
      return row.points ?? 0;
    case GroupStandingsTieBreakerEnum.POINTS_FOR:
      return row.pointsWon ?? 0;
    case GroupStandingsTieBreakerEnum.POINTS_AGAINST:
      return -(row.pointsAgainst ?? 0);
    case GroupStandingsTieBreakerEnum.GAME_DIFFERENCE:
      return row.gameDifference ?? (row.gameWin ?? 0) - (row.gameLost ?? 0);
    case GroupStandingsTieBreakerEnum.POINTS_PERCENTAGE: {
      if (row.pointsPercentage != null) {
        return row.pointsPercentage;
      }
      const pointsFor = row.pointsWon ?? 0;
      const pointsAgainst = row.pointsAgainst ?? 0;
      return pointsFor + pointsAgainst > 0
        ? pointsFor / (pointsFor + pointsAgainst)
        : 0;
    }
    case GroupStandingsTieBreakerEnum.SEEDING:
      return row.seed == null ? Number.NEGATIVE_INFINITY : -row.seed;
    case GroupStandingsTieBreakerEnum.HEAD_TO_HEAD:
      return getHeadToHeadWins(row, tiedTeamIds);
    case GroupStandingsTieBreakerEnum.HEAD_TO_HEAD_POINTS:
      return getHeadToHeadPointsDiff(row, tiedTeamIds);
    case GroupStandingsTieBreakerEnum.COIN_TOSS:
    default:
      return 0;
  }
};

export const normalizeGroupStandingsTieBreakers = (
  tieBreakers?: GroupStandingsTieBreakerEnum[] | null,
): GroupStandingsTieBreakerEnum[] => {
  if (!tieBreakers?.length) {
    return [...DEFAULT_GROUP_STANDINGS_TIE_BREAKERS];
  }

  const allowedValues = new Set(Object.values(GroupStandingsTieBreakerEnum));
  const normalized = tieBreakers.filter(
    (item, index, array) => allowedValues.has(item) && array.indexOf(item) === index,
  );

  return normalized.length
    ? normalized
    : [...DEFAULT_GROUP_STANDINGS_TIE_BREAKERS];
};

const splitByMetric = <T extends GroupStandingsComparableRow>(
  rows: IndexedRow<T>[],
  criterion: GroupStandingsTieBreakerEnum,
): IndexedRow<T>[][] => {
  if (rows.length <= 1) {
    return [rows];
  }

  if (criterion === GroupStandingsTieBreakerEnum.COIN_TOSS) {
    return rows.map((row) => [row]);
  }

  const tiedTeamIds = new Set(rows.map((item) => item.row.id));
  const sorted = [...rows].sort((left, right) => {
    const leftMetric = getMetric(left.row, criterion, tiedTeamIds);
    const rightMetric = getMetric(right.row, criterion, tiedTeamIds);
    if (rightMetric !== leftMetric) {
      return rightMetric - leftMetric;
    }
    if (left.row.id !== right.row.id) {
      return left.row.id - right.row.id;
    }
    return left.originalIndex - right.originalIndex;
  });

  const groups: IndexedRow<T>[][] = [];
  for (const row of sorted) {
    if (!groups.length) {
      groups.push([row]);
      continue;
    }

    const currentGroup = groups[groups.length - 1];
    const groupIds = new Set(currentGroup.map((item) => item.row.id));
    const metricIds = new Set(groupIds);
    metricIds.add(row.row.id);
    const groupMetric = getMetric(currentGroup[0].row, criterion, metricIds);
    const rowMetric = getMetric(row.row, criterion, metricIds);

    if (groupMetric === rowMetric) {
      currentGroup.push(row);
    } else {
      groups.push([row]);
    }
  }

  return groups;
};

export const sortGroupStandingsRows = <T extends GroupStandingsComparableRow>(
  rows: T[],
  tieBreakers?: GroupStandingsTieBreakerEnum[] | null,
): T[] => {
  if (!rows.length) {
    return [];
  }

  const normalizedTieBreakers = normalizeGroupStandingsTieBreakers(tieBreakers);
  let groupedRows: IndexedRow<T>[][] = [
    rows.map((row, originalIndex) => ({ row, originalIndex })),
  ];

  for (const tieBreaker of normalizedTieBreakers) {
    const nextGroups: IndexedRow<T>[][] = [];
    for (const group of groupedRows) {
      if (group.length === 1) {
        nextGroups.push(group);
        continue;
      }
      nextGroups.push(...splitByMetric(group, tieBreaker));
    }
    groupedRows = nextGroups;

    if (groupedRows.every((group) => group.length === 1)) {
      break;
    }
  }

  return groupedRows.flat().map((item) => item.row);
};
